<template>
  <div>
    <!-- Loading State -->
    <div v-if="$apollo.queries.task.loading" class="p-6">
      <div class="animate-pulse space-y-4">
        <div class="h-8 bg-gray-200 rounded w-3/4"></div>
        <div class="h-4 bg-gray-200 rounded w-full"></div>
        <div class="h-4 bg-gray-200 rounded w-5/6"></div>
      </div>
    </div>

    <!-- Task Content -->
    <div v-else-if="taskData" class="p-6">
      <!-- Header -->
      <div class="flex items-start justify-between mb-6">
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-2 flex-wrap">
            <template v-if="editingTitle">
              <input v-model="titleDraft" type="text"
                class="flex-1 text-2xl font-bold text-gray-900 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent min-w-0"
                @keydown.enter.prevent="saveTaskTitle" />
              <button type="button" :disabled="savingTitle" @click="saveTaskTitle"
                class="p-2 rounded-lg text-green-600 hover:bg-green-50 border border-green-200 disabled:opacity-50"
                title="Save title">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </button>
              <button type="button" :disabled="savingTitle" @click="cancelEditTitle"
                class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200 disabled:opacity-50"
                title="Cancel">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </template>
            <template v-else>
              <router-link :to="`/tasks/task/${taskId}`"
                class="text-2xl font-bold text-gray-900 hover:text-primary hover:underline transition-colors">
                {{ taskData.title }}
              </router-link>
              <button v-if="canEditTitleDescription" type="button" @click="startEditTitle"
                class="p-2 rounded-lg text-gray-500 hover:text-primary hover:bg-gray-100 border border-transparent hover:border-gray-200"
                title="Edit title">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                </svg>
              </button>
            </template>
            <span class="text-xs px-3 py-1 rounded-full text-white"
              :style="{ backgroundColor: getPriorityColor(taskData.priority) }">
              {{ taskData.priority }}
            </span>
          </div>
          <p v-if="!canEditTitleDescription && taskData.description" class="text-gray-600 mb-2">{{ taskData.description }}</p>
          <div v-if="canEditTitleDescription" class="mb-2">
            <div v-if="editingDescription">
              <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea v-model="descriptionDraft" rows="4"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-700"
                placeholder="Add a description (optional)"></textarea>
              <div class="flex gap-2 mt-2">
                <button type="button" :disabled="savingDescription" @click="saveTaskDescription"
                  class="p-2 rounded-lg text-green-600 hover:bg-green-50 border border-green-200 disabled:opacity-50"
                  title="Save description">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </button>
                <button type="button" :disabled="savingDescription" @click="cancelEditDescription"
                  class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200 disabled:opacity-50"
                  title="Cancel">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div v-else class="flex items-start gap-2">
              <p v-if="taskData.description" class="text-gray-600 flex-1 break-words">{{ taskData.description }}</p>
              <p v-else class="text-gray-500 italic text-sm flex-1">No description yet.</p>
              <button type="button" @click="startEditDescription"
                class="p-2 rounded-lg text-gray-500 hover:text-primary hover:bg-gray-100 border border-transparent hover:border-gray-200 flex-shrink-0"
                title="Edit description">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                </svg>
              </button>
            </div>
          </div>
          <p v-if="taskData.created_by && taskData.created_by.name" class="text-sm text-gray-500 mt-2">
            Created by <span class="font-medium text-gray-700">{{ taskData.created_by.name }}</span>
          </p>
        </div>
        <div class="flex items-center gap-2 ml-4">
          <button v-if="canDeleteTask" @click="confirmDeleteTask"
            class="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
            title="Delete task">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
              </path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Checklist -->
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">Checklist</h3>
        <div v-if="canEditChecklist" class="flex gap-2 mb-3">
          <input v-model="newChecklistItem" type="text"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Add checklist item..." @keydown.enter.prevent="addChecklistItem" />
          <button type="button" @click="addChecklistItem"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Add
          </button>
        </div>
        <div v-if="editForm.checklist_items && editForm.checklist_items.length > 0" class="space-y-2">
          <div v-for="(item, index) in editForm.checklist_items" :key="item.id || `new-${index}`"
            class="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
            <button v-if="canEditChecklist" type="button" @click="toggleChecklistItem(index)"
              class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
              :class="item.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-400 hover:border-gray-500'">
              <svg v-if="item.is_completed" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"></path>
              </svg>
            </button>
            <span v-else class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center"
              :class="item.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-400'">
              <svg v-if="item.is_completed" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"></path>
              </svg>
            </span>
            <span class="flex-1 text-gray-700" :class="{ 'line-through text-gray-500': item.is_completed }">{{
              item.title }}</span>
            <button v-if="canEditChecklist" type="button" @click="removeChecklistItem(index)"
              class="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50" title="Remove">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 italic">No checklist items yet.<span v-if="canEditChecklist"> Add one
            above.</span></p>
      </div>

      <!-- Time Tracking & Progress -->
      <div v-if="taskData" class="mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">Time & Progress</h3>
        <div class="bg-gray-50 rounded-xl p-5 space-y-5 border border-gray-100">
          <!-- Estimated Time & Time Progress Bar (checklist excluded from time progress) -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Estimated Time</label>
              <div v-if="canEditEstimatedTime" class="space-y-2">
                <div class="flex gap-2">
                  <button type="button" @click="setEditEstimatedTimeType('time')"
                    :class="[
                      'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      editForm.estimated_time_type === 'time'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    ]">
                    Time
                  </button>
                  <button type="button" @click="setEditEstimatedTimeType('days')"
                    :class="[
                      'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      editForm.estimated_time_type === 'days'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    ]">
                    Days
                  </button>
                </div>
                <div v-if="editForm.estimated_time_type === 'time'" class="flex gap-2">
                  <div class="flex-1">
                    <input v-model.number="editForm.estimated_hours" type="number" min="0" placeholder="Hours"
                      @change="updateTask"
                      class="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary/30 bg-white" />
                  </div>
                  <div class="flex-1">
                    <input v-model.number="editForm.estimated_minutes_input" type="number" min="0" max="59" placeholder="Minutes"
                      @change="updateTask"
                      class="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary/30 bg-white" />
                  </div>
                </div>
                <div v-else class="flex gap-2 items-center">
                  <input v-model.number="editForm.estimated_days" type="number" min="1" placeholder="e.g. 2"
                    @change="updateTask"
                    class="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary/30 bg-white" />
                  <span class="text-sm text-gray-500 flex-shrink-0">days</span>
                </div>
              </div>
              <div v-else class="px-4 py-2.5 bg-gray-50 rounded-lg text-gray-700">
                {{ formatEstimatedDisplay(taskData.estimated_minutes || 0, taskData.estimated_time_type) }}
              </div>
            </div>
            <div v-if="displayTimeProgressPercent != null" class="flex items-end">
              <div class="flex-1">
                <div class="flex justify-between text-xs text-gray-600 mb-1.5">
                  <span>Time Progress</span>
                  <span class="time-display-tabular">{{ displayTimeProgressPercent }}%</span>
                </div>
                <div class="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div class="task-progress-bar-fill h-full rounded-full transition-all duration-500 ease-out"
                    :class="[timeProgressBarColorClass, { 'task-progress-bar-fill--animated': (taskData.status || '').toLowerCase() === 'in_progress' }, { 'task-progress-bar-fill--static': (taskData.status || '').toLowerCase() === 'paused' }]"
                    :style="{ width: Math.min(100, Math.max(0, displayTimeProgressPercent)) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Checklist Progress Bar (separate, only when checklist exists) -->
          <div v-if="checklistProgressPercent != null" class="pt-2 border-t border-gray-200/80">
            <div class="flex justify-between text-xs text-gray-600 mb-1.5">
              <span>Checklist Progress</span>
              <span class="time-display-tabular">{{ checklistProgressPercent }}%</span>
            </div>
            <div class="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div class="h-full rounded-full transition-all duration-500 ease-out bg-emerald-500"
                :style="{ width: Math.min(100, Math.max(0, checklistProgressPercent)) + '%' }"></div>
            </div>
          </div>

          <!-- Time spent & status (timer is automatic: starts on In Progress, pauses on Pause, stops on In Review/Done) -->
          <div v-if="isInProgressColumn" class="flex items-center gap-4 flex-wrap pt-1 border-t border-gray-200/80">
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-500">Time spent</span>
              <span class="time-display-tabular text-sm font-semibold text-gray-800 min-w-[4.5rem]">{{
                formatTimeSpent(displayActualSeconds) }}</span>
            </div>
            <span v-if="taskData.status === 'paused'"
              class="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium border border-amber-200/60">
              <span class="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
              Paused
            </span>
            <span v-else-if="taskData.is_timer_running"
              class="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium border border-emerald-200/60">
              <span class="timer-dot w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0"></span>
              Going on
            </span>
          </div>

          <!-- Completed date (shown when task is Done) -->
          <div v-if="taskData.status === 'done' && taskData.completed_at"
            class="flex items-center gap-2 text-sm text-green-700 font-medium">
            <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Completed on {{ formatDate(taskData.completed_at) }}
          </div>

          <!-- Time Exceeded / Early Finish (shown when task is In Review or Done) -->
          <div v-if="taskData.status === 'done' || taskData.status === 'in_review'"
            class="flex flex-wrap gap-4 text-sm">
            <div v-if="taskData.time_exceeded_minutes != null && taskData.time_exceeded_minutes > 0"
              class="text-red-600 font-medium">
              Exceeded by {{ formatMinutesOrDays(taskData.time_exceeded_minutes, taskData.estimated_time_type, true) }}
            </div>
            <div v-else-if="taskData.early_finish_minutes != null && taskData.early_finish_minutes > 0"
              class="text-green-600 font-medium">
              Finished {{ formatMinutesOrDays(taskData.early_finish_minutes, taskData.estimated_time_type, false) }} early
            </div>
          </div>
        </div>
      </div>

      <!-- Task Details Grid: Bucket, Status, Priority, Due Date -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div class="flex flex-col" :class="{ 'create-task-invalid': !bucketValidation.isValid }">
          <label class="block text-sm font-medium text-gray-700 mb-2">Bucket *</label>
          <select v-model="editForm.column_id" @change="updateTask" :disabled="!canEditBucket"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed">
            <option value="">Select bucket...</option>
            <option v-for="column in boardColumns" :key="column.id" :value="column.id">
              {{ column.name }}
            </option>
          </select>
          <transition name="fade" mode="out-in">
            <p v-if="!bucketValidation.isValid" class="text-xs text-red-600 mt-1">{{ bucketValidation.msg }}</p>
          </transition>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select v-model="editForm.status" @change="updateTask" :disabled="!canEditStatus"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed">
            <option value="todo" :disabled="assigneeCannotRevertToTodo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option v-if="showPauseOption" value="paused">Pause</option>
            <option value="in_review">In Review</option>
            <option value="done">Done</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Priority</label>
          <select v-model="editForm.priority" @change="updateTask" :disabled="!canEditPriority"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
          <input v-model="editForm.due_date" type="date" @change="updateTask" :disabled="!canEditDueDate"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed" />
        </div>
      </div>

      <!-- Assignee & Viewers -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Assignees</label>
          <multiselect v-model="editForm.assignees" :options="boardMembersList" :multiple="true" :close-on-select="false"
            :clear-on-select="false" :disabled="!canEditAssignee" placeholder="Select assignees (optional)" label="name"
            track-by="id" class="w-full" @select="updateTask" @remove="updateTask">
            <template #option="{ option }">
              <div class="flex flex-col py-1">
                <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
                <span class="text-xs text-gray-500">{{ option.email }}</span>
              </div>
            </template>
            <template #noResult>
              <span class="text-sm text-gray-500">No members found.</span>
            </template>
          </multiselect>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Viewers</label>
          <multiselect v-model="editForm.viewers" :options="boardMembersList" :multiple="true" :close-on-select="false"
            :disabled="!canEditViewers" placeholder="Select people to notify on status changes (optional)" label="name"
            track-by="id" class="w-full" @select="updateTask" @remove="updateTask">
            <template #option="{ option }">
              <div class="flex flex-col py-1">
                <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
                <span class="text-xs text-gray-500">{{ option.email }}</span>
              </div>
            </template>
            <template #noResult>
              <span class="text-sm text-gray-500">No members found.</span>
            </template>
          </multiselect>
          <p class="text-xs text-gray-500 mt-1">Viewers receive notifications when the task status changes.</p>
        </div>
      </div>

      <!-- Attachments (full width) -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
        <div class="space-y-2">
          <!-- Upload New Attachment -->
          <div v-if="canEditAttachments">
            <input ref="fileInput" type="file" multiple @change="handleFileSelect" class="hidden"
              id="task-attachment-upload" />
            <label for="task-attachment-upload"
              class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors cursor-pointer text-sm">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>Add Attachment</span>
            </label>
          </div>

          <!-- Attachments List -->
          <div class="space-y-2">
            <div v-for="attachment in taskData.attachments" :key="attachment.id"
              class="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <svg class="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z">
                  </path>
                </svg>
                <div class="flex-1 min-w-0">
                  <span class="text-sm text-gray-700 block truncate">{{ attachment.file_name }}</span>
                  <span class="text-xs text-gray-500">{{ formatFileSize(attachment.file_size) }}</span>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button @click="viewAttachment(attachment)"
                  class="text-green-500 hover:text-green-700 p-1.5 rounded-lg hover:bg-green-50 transition-colors"
                  title="View attachment">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z">
                    </path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                    </path>
                  </svg>
                </button>
                <button @click="downloadAttachment(attachment)"
                  class="text-blue-500 hover:text-blue-700 p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                  title="Download attachment">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                  </svg>
                </button>
                <button v-if="canDeleteAttachment(attachment)" @click="deleteAttachment(attachment.id)"
                  class="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                  title="Delete attachment">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                    </path>
                  </svg>
                </button>
              </div>
            </div>
            <div v-if="taskData.attachments.length === 0" class="text-sm text-gray-500 text-center py-4">
              No attachments yet
            </div>
          </div>
        </div>
      </div>

      <!-- Comments Section -->
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Comments</h3>

        <!-- Add Comment -->
        <div v-if="canAddComments" class="mb-4">
          <textarea v-model="newComment" rows="3"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary mb-2"
            placeholder="Add a comment..."></textarea>
          <button @click="addComment" :disabled="!newComment.trim() || commentLoading"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50">
            {{ commentLoading ? 'Posting...' : 'Post Comment' }}
          </button>
        </div>

        <!-- Comments List -->
        <div class="space-y-4">
          <div v-for="comment in taskData.allComments" :key="comment.id" class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <div
                class="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs flex-shrink-0 overflow-hidden">
                <img v-if="comment.user && comment.user.profile_picture_url"
                  :src="getProfilePicture(comment.user.profile_picture_url)" :alt="comment.user.name"
                  class="w-full h-full object-cover" @error="handleCommentAvatarError">
                <span v-else>{{ getInitials(comment.user && comment.user.name) }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-gray-900">{{ (comment.user && comment.user.name) || 'Unknown'
                    }}</span>
                    <span class="text-xs text-gray-500">{{ formatDateTime(comment.created_at) }}</span>
                  </div>
                  <div v-if="isCommentOwner(comment)" class="flex items-center gap-1">
                    <button v-if="editingCommentId !== comment.id" type="button" @click="startEditComment(comment)"
                      class="text-gray-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors"
                      title="Edit comment">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                        </path>
                      </svg>
                    </button>
                    <button v-if="editingCommentId !== comment.id" type="button" @click="confirmDeleteComment(comment)"
                      class="text-gray-500 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                      title="Delete comment">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                        </path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div v-if="editingCommentId === comment.id" class="mt-2">
                  <textarea v-model="editingCommentText" rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"></textarea>
                  <div class="flex gap-2 mt-2">
                    <button type="button" @click="saveEditComment"
                      class="px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-600 text-sm">
                      Save
                    </button>
                    <button type="button" @click="cancelEditComment"
                      class="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
                      Cancel
                    </button>
                  </div>
                </div>
                <p v-else class="text-gray-700">{{ comment.comment }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Activity Log (visible only with task-log-history permission) -->
      <div v-if="hasTaskLogHistoryPermission" class="mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">Activity Log</h3>
        <div v-if="activityLogsLoading" class="text-sm text-gray-500 py-4">Loading activity log...</div>
        <div v-else-if="activityLogsError" class="text-sm text-amber-600 py-4">{{ activityLogsError }}</div>
        <div v-else-if="activityLogs && activityLogs.length > 0"
          class="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
          <div class="divide-y divide-gray-200 max-h-80 overflow-y-auto">
            <div v-for="log in activityLogs" :key="log.id"
              class="flex gap-3 px-4 py-3 hover:bg-gray-100/50 transition-colors">
              <div
                class="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-xs flex-shrink-0">
                {{ getInitials(log.user && log.user.name) }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm text-gray-800">{{ log.description }}</p>
                <p class="text-xs text-gray-500 mt-0.5">
                  {{ (log.user && log.user.name) || 'Unknown' }} · {{ formatDateTime(log.created_at) }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 italic py-4">No activity recorded yet.</p>
      </div>
    </div>
  </div>

  <!-- Preview Modal -->
  <div v-if="showPreviewModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    @click.self="closePreviewModal">
    <div class="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-90vh overflow-y-auto flex flex-col">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 class="text-xl font-bold text-gray-900">{{ previewModalTitle }}</h2>
        <button @click="closePreviewModal"
          class="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-50 transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Preview Content -->
      <div class="preview-container flex-1 overflow-y-auto p-6">
        <!-- Loading State -->
        <div v-if="previewLoading" class="flex justify-center items-center h-full">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p class="text-gray-600">Loading preview...</p>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="previewError" class="text-center py-8">
          <svg class="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z">
            </path>
          </svg>
          <p class="text-red-600 mb-4">{{ previewError }}</p>
          <button @click="downloadAttachment(previewAttachment)"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Download Instead
          </button>
        </div>

        <!-- Preview Content -->
        <div v-else class="preview-content">
          <!-- PDF Preview -->
          <div v-if="isPdfFile(previewAttachment.file_type)" class="pdf-preview">
            <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg" frameborder="0"></iframe>
          </div>

          <!-- Image Preview -->
          <div v-else-if="isImageFile(previewAttachment.file_type)" class="image-preview text-center">
            <img :src="previewUrl" :alt="previewAttachment.file_name"
              class="max-w-full max-h-96 mx-auto rounded-lg shadow-lg" />
          </div>

          <!-- Text File Preview -->
          <div v-else-if="isTextFile(previewAttachment.file_type)" class="text-preview">
            <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg" frameborder="0"></iframe>
          </div>

          <!-- DOCX and Office File Preview -->
          <div v-else-if="isDocxFile(previewAttachment.file_type) || isOfficeDocument(previewAttachment.file_type)"
            class="office-preview">
            <!-- Microsoft Office Online Viewer -->
            <div v-if="previewUrl && !useMammothFallback">
              <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg" frameborder="0"></iframe>
            </div>

            <!-- Mammoth HTML Conversion (Fallback for DOCX) -->
            <div v-else-if="useMammothFallback && previewHtml && isDocxFile(previewAttachment.file_type)" class="p-6">
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div class="flex items-start">
                  <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div class="text-sm text-blue-800">
                    <p class="font-medium mb-1">Using HTML Preview</p>
                    <p>Microsoft Office Online viewer is not available. Showing converted HTML preview. Some
                      formatting
                      may differ from the original document.</p>
                  </div>
                </div>
              </div>
              <div class="docx-wrapper mx-4">
                <div v-html="previewHtml" class="docx-content prose max-w-none"></div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-else class="text-center py-8">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p class="text-gray-600">Loading document preview...</p>
            </div>
          </div>

          <!-- Fallback for other file types -->
          <div v-else class="fallback-preview text-center py-8">
            <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z">
              </path>
            </svg>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ previewAttachment.file_name }}</h3>
            <p class="text-gray-600 mb-4">
              Preview not available for this file type. Please download to view the content.
            </p>
            <button @click="downloadAttachment(previewAttachment)"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Download File
            </button>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div v-if="!previewLoading && !previewError" class="flex justify-end p-6 border-t border-gray-200 bg-white">
        <button @click="downloadAttachment(previewAttachment)"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
          </svg>
          Download
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import mammoth from 'mammoth';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  name: 'task-detail-modal',
  components: {
    Multiselect,
  },
  props: {
    taskId: {
      type: Number,
      required: true,
    },
    boardMembers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      newComment: '',
      newChecklistItem: '',
      commentLoading: false,
      attachmentLoading: false,
      bucketValidation: { isValid: true, msg: '' },
      editingTitle: false,
      editingDescription: false,
      titleDraft: '',
      descriptionDraft: '',
      savingTitle: false,
      savingDescription: false,
      editForm: {
        column_id: '',
        status: '',
        priority: '',
        due_date: '',
        story_points: null,
        estimated_minutes: null,
        estimated_time_type: 'time',
        estimated_hours: 0,
        estimated_minutes_input: 0,
        estimated_days: '',
        assignees: [],
        viewers: [],
        checklist_items: [],
      },
      showPreviewModal: false,
      previewAttachment: null,
      previewUrl: null,
      previewHtml: null,
      previewLoading: false,
      previewError: null,
      useMammothFallback: false,
      editingCommentId: null,
      editingCommentText: '',
      progressTick: 0,
      progressUpdater: null,
      progressSnapshot: null,
      activityLogs: [],
      activityLogsError: null,
    };
  },
  computed: {
    taskData() {
      if (!this.task || this.task.error) {
        return null;
      }
      return this.task.data;
    },
    currentUserId() {
      return this.$store.getters.userId;
    },
    canDeleteTask() {
      if (!this.taskData || !this.taskData.created_by || !this.currentUserId) {
        return false;
      }
      // Check if current user is the creator
      return this.taskData.created_by.id && parseInt(this.taskData.created_by.id) === parseInt(this.currentUserId);
    },
    hasTaskLogHistoryPermission() {
      const permissions = this.$store.getters.permissions || '';
      const permissionsArray = permissions.split(',').map(p => p.trim());
      return permissionsArray.includes('task-log-history');
    },
    activityLogsLoading() {
      const q = this.$apollo && this.$apollo.queries && this.$apollo.queries.taskActivityLogs;
      return q ? q.loading : false;
    },
    isCreator() {
      if (!this.taskData || !this.taskData.created_by || !this.taskData.created_by.id || !this.currentUserId) return false;
      return parseInt(this.taskData.created_by.id) === parseInt(this.currentUserId);
    },
    isAssignee() {
      if (!this.taskData || !this.taskData.assignees || !this.currentUserId) return false;
      return this.taskData.assignees.some(a => parseInt(a.id) === parseInt(this.currentUserId));
    },
    isViewer() {
      if (!this.taskData || !this.taskData.viewers || !this.currentUserId) return false;
      return this.taskData.viewers.some(v => parseInt(v.id) === parseInt(this.currentUserId));
    },
    isOtherMember() {
      return !this.isCreator && !this.isAssignee && !this.isViewer;
    },
    isToDoStatus() {
      return ((this.taskData && this.taskData.status) || '').toLowerCase() === 'todo';
    },
    /** Creator may edit title/description; assignees/viewers cannot (backend TaskPermission). */
    canEditTitleDescription() {
      return this.isCreator;
    },
    canEditStatus() {
      if (this.isOtherMember) return false;
      return this.isCreator || this.isAssignee;
    },
    canEditBucket() {
      if (this.isOtherMember) return false;
      return this.isCreator || this.isAssignee;
    },
    boardColumns() {
      const board = this.taskData && this.taskData.board;
      if (!board || !board.columns) return [];
      return (board.columns || []).slice().sort(function (a, b) { return (a.order || 0) - (b.order || 0); });
    },
    canEditPriority() {
      if (this.isOtherMember) return false;
      return this.isCreator;
    },
    canEditDueDate() {
      if (this.isOtherMember) return false;
      return this.isCreator;
    },
    canEditAssignee() {
      if (this.isOtherMember) return false;
      return this.isCreator;
    },
    canEditViewers() {
      if (this.isOtherMember) return false;
      return this.isCreator;
    },
    canEditChecklist() {
      if (this.isOtherMember) return false;
      return this.isCreator || this.isAssignee;
    },
    canEditAttachments() {
      if (this.isOtherMember) return false;
      return this.isCreator || this.isAssignee || this.isViewer;
    },
    canEditEstimatedTime() {
      if (this.isOtherMember) return false;
      return this.isCreator;
    },
    isInProgressColumn() {
      const s = (this.taskData && this.taskData.status || '').toLowerCase();
      return s === 'in_progress' || s === 'paused';
    },
    showPauseOption() {
      const s = (this.taskData && this.taskData.status || '').toLowerCase();
      return s === 'in_progress' || s === 'paused';
    },
    assigneeCannotRevertToTodo() {
      if (!this.taskData || !this.isAssignee || this.isCreator) return false;
      const s = (this.taskData.status || '').toLowerCase();
      return s === 'in_progress' || s === 'paused';
    },
    displayActualSeconds() {
      if (!this.taskData) return 0;
      if (this.taskData.is_timer_running && this.progressSnapshot) {
        this.progressTick;
        const elapsedMs = Date.now() - this.progressSnapshot.fetchTime;
        const extraSeconds = elapsedMs / 1000;
        return Math.floor(this.progressSnapshot.actualSeconds + extraSeconds);
      }
      return this.taskData.actual_seconds_spent || 0;
    },
    displayTimeProgressPercent() {
      if (!this.taskData) return null;
      const estimatedMinutes = this.taskData.estimated_minutes || 0;
      const estimatedSeconds = estimatedMinutes * 60;
      if (estimatedSeconds <= 0) return null;
      if (this.taskData.is_timer_running && this.progressSnapshot) {
        this.progressTick;
        const elapsedMs = Date.now() - this.progressSnapshot.fetchTime;
        const extraSeconds = elapsedMs / 1000;
        const realtimeSeconds = this.progressSnapshot.actualSeconds + extraSeconds;
        const timeProgress = Math.min(100, (realtimeSeconds / estimatedSeconds) * 100);
        return Math.round(timeProgress * 10) / 10;
      }
      const actualSeconds = this.taskData.actual_seconds_spent || 0;
      const timeProgress = Math.min(100, (actualSeconds / estimatedSeconds) * 100);
      return Math.round(timeProgress * 10) / 10;
    },
    checklistProgressPercent() {
      const items = this.editForm.checklist_items || [];
      if (items.length === 0) return null;
      const completed = items.filter(i => i.is_completed).length;
      return Math.round((completed / items.length) * 1000) / 10;
    },
    timeProgressBarColorClass() {
      const p = this.displayTimeProgressPercent;
      if (p == null) return 'bg-blue-500';
      if (p >= 90) return 'bg-red-500';
      if (p >= 80) return 'bg-amber-500';
      return 'bg-blue-500';
    },
    canAddComments() {
      if (this.isOtherMember) return false;
      return this.isCreator || this.isAssignee || this.isViewer;
    },
    boardMembersList() {
      // Use prop when provided (e.g. from board/member modal), else use task's board members (standalone page)
      if (this.boardMembers && this.boardMembers.length > 0) {
        return this.boardMembers;
      }
      return (this.taskData && this.taskData.board && this.taskData.board.members) || [];
    },
    previewModalTitle() {
      return this.previewAttachment ? `Preview: ${this.previewAttachment.file_name}` : 'Preview Attachment';
    },
  },
  watch: {
    taskData: {
      handler(newData) {
        if (!newData) {
          this.progressSnapshot = null;
          this.stopProgressUpdater();
          return;
        }
        let assignees = [];
        if (newData.assignees && newData.assignees.length > 0) {
          assignees = newData.assignees.map(function (a) {
            const found = this.boardMembersList.find(function (member) {
              return member.id === a.id || parseInt(member.id) === parseInt(a.id);
            });
            return found || a;
          }.bind(this));
        }

        // Set viewers from taskData.viewers
        let viewers = [];
        if (newData.viewers && newData.viewers.length > 0) {
          viewers = newData.viewers.map(v => {
            const found = this.boardMembersList.find(member =>
              member.id === v.id || parseInt(member.id) === parseInt(v.id)
            );
            return found || v;
          });
        }

        const estMin = newData.estimated_minutes || 0;
        const estType = (newData.estimated_time_type || 'time');
        const isDays = estType === 'days';
        this.editForm = {
          column_id: newData.column_id || (newData.column && newData.column.id) || '',
          status: newData.status || '',
          priority: newData.priority || '',
          due_date: newData.due_date || '',
          story_points: newData.story_points || null,
          estimated_minutes: estMin || null,
          estimated_time_type: estType,
          estimated_hours: isDays ? 0 : Math.floor(estMin / 60),
          estimated_minutes_input: isDays ? 0 : estMin % 60,
          estimated_days: isDays ? (estMin / 1440) : '',
          assignees: assignees,
          viewers: viewers,
          checklist_items: (newData.checklist_items || []).map(item => ({
            id: item.id,
            title: item.title,
            is_completed: item.is_completed || false,
          })),
        };

        if (newData.is_timer_running) {
          this.progressSnapshot = {
            actualSeconds: newData.actual_seconds_spent || 0,
            fetchTime: Date.now(),
          };
          this.startProgressUpdater();
        } else {
          this.progressSnapshot = null;
          this.stopProgressUpdater();
        }
      },
      deep: true,
    },
  },
  beforeUnmount() {
    this.stopProgressUpdater();
  },
  apollo: {
    task: {
      query: gql`
        query GetTask($id: ID!) {
          task(id: $id) {
            error
            error_code
            message
            data {
              id
              token
              title
              description
              priority
              status
              due_date
              story_points
              estimated_minutes
              estimated_time_type
              actual_minutes_spent
              actual_seconds_spent
              completed_at
              time_exceeded_minutes
              early_finish_minutes
              progress_percent
              is_timer_running
              labels
              assignees {
                id
                name
                email
              }
              viewers {
                id
                name
                email
              }
              created_by {
                id
                name
              }
              allComments {
                id
                comment
                created_at
                user {
                  id
                  name
                  email
                  profile_picture_url
                }
              }
              attachments {
                id
                file_name
                file_path
                file_type
                file_size
                user_id
                user {
                  id
                  name
                }
              }
              column_id
              column { id name order }
              board {
                id
                columns { id name order }
                members {
                  id
                  name
                  email
                }
              }
              checklist_items {
                id
                title
                is_completed
                order
              }
            }
          }
        }
      `,
      variables() {
        return {
          id: this.taskId,
        };
      },
      fetchPolicy: 'cache-and-network',
    },
    taskActivityLogs: {
      query: gql`
        query GetTaskActivityLogs($task_id: ID!, $first: Int) {
          taskActivityLogs(task_id: $task_id, first: $first) {
            id
            task_id
            user_id
            action
            description
            meta
            created_at
            user {
              id
              name
              email
            }
          }
        }
      `,
      variables() {
        return {
          task_id: this.taskId,
          first: 50,
        };
      },
      skip() {
        return !this.hasTaskLogHistoryPermission || !this.taskId;
      },
      fetchPolicy: 'cache-and-network',
      result({ data }) {
        this.activityLogsError = null;
        if (data && data.taskActivityLogs) {
          this.activityLogs = data.taskActivityLogs;
        }
      },
      error(error) {
        this.activityLogs = [];
        this.activityLogsError = (error && error.message) ? error.message : 'Failed to load activity log';
      },
    },
  },
  methods: {
    getPriorityColor(priority) {
      const colors = {
        low: '#10B981',
        medium: '#499DD4',
        high: '#F59E0B',
        critical: '#EF4444',
      };
      return colors[priority] || '#9BA4B5';
    },
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    getProfilePicture(url) {
      if (!url) return '/img/default-avatar.png';
      if (url.indexOf('http') === 0) return url;
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    handleCommentAvatarError(event) {
      event.target.src = '/img/default-avatar.png';
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getAttachmentUrl(filePath) {
      // Adjust this based on your file storage configuration
      const baseUrl = process.env.VUE_APP_PUBLIC_URL || '';
      return `${baseUrl}/storage/${filePath}`;
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    formatMinutes(minutes) {
      if (!minutes || minutes < 0) return '0m';
      if (minutes < 60) return minutes + 'm';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? h + 'h ' + m + 'm' : h + 'h';
    },
    formatEstimatedDisplay(minutes, type) {
      if (!minutes || minutes < 0) return '0m';
      if (type === 'days') {
        const d = minutes / 1440;
        return d === 1 ? '1 day' : Math.round(d) + ' days';
      }
      return this.formatMinutes(minutes);
    },
    formatMinutesOrDays(minutes, type, roundUp) {
      if (!minutes || minutes < 0) return '0m';
      if (type === 'days') {
        const d = roundUp ? Math.ceil(minutes / 1440) : Math.floor(minutes / 1440);
        if (d === 0) return '0m';
        return d === 1 ? '1 day' : d + ' days';
      }
      return this.formatMinutes(minutes);
    },
    formatTimeSpent(seconds) {
      if (seconds == null || seconds < 0) return '0s';
      const s = Math.floor(seconds);
      if (s < 60) return s + 's';
      const m = Math.floor(s / 60);
      const secs = s % 60;
      if (m < 60) return secs > 0 ? m + 'm ' + secs + 's' : m + 'm';
      const h = Math.floor(m / 60);
      const mins = m % 60;
      return mins > 0 || secs > 0 ? h + 'h ' + mins + 'm ' + secs + 's' : h + 'h';
    },
    startProgressUpdater() {
      this.stopProgressUpdater();
      this.progressUpdater = setInterval(() => {
        this.progressTick += 1;
      }, 1000);
    },
    stopProgressUpdater() {
      if (this.progressUpdater) {
        clearInterval(this.progressUpdater);
        this.progressUpdater = null;
      }
    },
    canDeleteAttachment(attachment) {
      if (!attachment || !this.currentUserId) {
        return false;
      }
      // Only the person who uploaded the attachment can delete it
      return this.canEditAttachments && attachment.user_id && parseInt(attachment.user_id) === parseInt(this.currentUserId);
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        this.uploadAttachments(files);
      }
      // Reset input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async uploadAttachments(files) {
      this.attachmentLoading = true;
      try {
        for (const file of files) {
          await this.$apollo.mutate({
            mutation: gql`
              mutation UploadTaskAttachment($task_id: ID!, $file: Upload!) {
                uploadTaskAttachment(task_id: $task_id, file: $file) {
                  error
                  error_code
                  message
                  data {
                    id
                    file_name
                  }
                }
              }
            `,
            variables: {
              task_id: this.taskId,
              file: file,
            },
          });
        }
        // Refetch task data to show new attachments
        this.$apollo.queries.task.refetch();
        if (this.hasTaskLogHistoryPermission && this.$apollo.queries.taskActivityLogs) {
          this.$apollo.queries.taskActivityLogs.refetch();
        }
      } catch (error) {
        console.error('Error uploading attachment:', error);
        alert('Failed to upload attachment. Please try again.');
      } finally {
        this.attachmentLoading = false;
      }
    },
    async deleteAttachment(attachmentId) {
      // Find attachment to get file name for confirmation message
      const attachment = this.taskData.attachments.find(a => a.id === attachmentId);
      const fileName = attachment ? attachment.file_name : 'this attachment';

      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete "${fileName}". This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTaskAttachment($id: ID!) {
              deleteTaskAttachment(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: attachmentId,
          },
        });

        const payload = data && data.deleteTaskAttachment;
        if (payload && payload.error) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: payload.message || 'Failed to delete attachment.'
          });
          return;
        }

        // Show success toast
        this.showToast(payload && payload.message || 'Attachment deleted successfully');

        // Refetch task data to remove deleted attachment
        this.$apollo.queries.task.refetch();
        if (this.hasTaskLogHistoryPermission && this.$apollo.queries.taskActivityLogs) {
          this.$apollo.queries.taskActivityLogs.refetch();
        }
      } catch (error) {
        console.error('Error deleting attachment:', error);
        await this.$swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Failed to delete attachment. Please try again.'
        });
      }
    },
    setEditEstimatedTimeType(type) {
      if (this.editForm.estimated_time_type === type) return;
      this.editForm.estimated_time_type = type;
      if (type === 'time') {
        this.editForm.estimated_hours = 0;
        this.editForm.estimated_minutes_input = 0;
      } else {
        this.editForm.estimated_days = '';
      }
    },
    getEditFormEstimatedMinutes() {
      if (this.editForm.estimated_time_type === 'time') {
        const h = parseInt(this.editForm.estimated_hours, 10) || 0;
        const m = parseInt(this.editForm.estimated_minutes_input, 10) || 0;
        return h * 60 + m;
      }
      const d = parseInt(this.editForm.estimated_days, 10);
      if (isNaN(d) || d < 1) return null;
      return d * 24 * 60;
    },
    startEditTitle() {
      if (!this.canEditTitleDescription || !this.taskData) return;
      this.titleDraft = this.taskData.title || '';
      this.editingTitle = true;
    },
    cancelEditTitle() {
      this.editingTitle = false;
      this.titleDraft = '';
    },
    async saveTaskTitle() {
      if (!this.canEditTitleDescription) return;
      const trimmed = (this.titleDraft || '').trim();
      if (!trimmed) {
        if (this.$swal) {
          await this.$swal.fire({ icon: 'warning', title: 'Title required', text: 'Task title cannot be empty.' });
        }
        return;
      }
      this.savingTitle = true;
      const ok = await this.mutateTaskUpdate({ title: trimmed });
      this.savingTitle = false;
      if (ok) {
        this.editingTitle = false;
        this.titleDraft = '';
      }
    },
    startEditDescription() {
      if (!this.canEditTitleDescription || !this.taskData) return;
      this.descriptionDraft = this.taskData.description != null ? String(this.taskData.description) : '';
      this.editingDescription = true;
    },
    cancelEditDescription() {
      this.editingDescription = false;
      this.descriptionDraft = '';
    },
    async saveTaskDescription() {
      if (!this.canEditTitleDescription) return;
      const desc = this.descriptionDraft != null ? String(this.descriptionDraft) : '';
      this.savingDescription = true;
      const ok = await this.mutateTaskUpdate({ description: desc });
      this.savingDescription = false;
      if (ok) {
        this.editingDescription = false;
        this.descriptionDraft = '';
      }
    },
    async mutateTaskUpdate(input) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTask($id: ID!, $input: UpdateTaskInput!) {
              updateTask(id: $id, input: $input) {
                error
                error_code
                message
                data {
                  id
                }
              }
            }
          `,
          variables: {
            id: this.taskId,
            input,
          },
        });

        if (data.updateTask.error) {
          if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Cannot update task', text: data.updateTask.message });
          return false;
        }
        this.$apollo.queries.task.refetch();
        if (this.hasTaskLogHistoryPermission && this.$apollo.queries.taskActivityLogs) {
          this.$apollo.queries.taskActivityLogs.refetch();
        }
        this.$emit('updated');
        return true;
      } catch (error) {
        console.error('Error updating task:', error);
        if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update task. Please try again.' });
        return false;
      }
    },
    async updateTask() {
      try {
        const computedEst = this.canEditEstimatedTime ? this.getEditFormEstimatedMinutes() : this.editForm.estimated_minutes;
        const input = {
          column_id: this.editForm.column_id ? parseInt(this.editForm.column_id, 10) : null,
          status: this.editForm.status,
          priority: this.editForm.priority,
          due_date: this.editForm.due_date || null,
          story_points: this.editForm.story_points || null,
          estimated_minutes: (computedEst != null && computedEst >= 1) ? parseInt(computedEst, 10) : null,
          estimated_time_type: this.editForm.estimated_time_type || 'time',
        };

        // Handle assignee_ids
        if (this.editForm.assignees && this.editForm.assignees.length > 0) {
          input.assignee_ids = this.editForm.assignees.map(function (a) {
            return parseInt(a.id, 10);
          });
        } else {
          input.assignee_ids = [];
        }
        // Handle viewer_ids
        if (this.editForm.viewers && this.editForm.viewers.length > 0) {
          input.viewer_ids = this.editForm.viewers.map(v => parseInt(v.id));
        } else {
          input.viewer_ids = [];
        }
        // Handle checklist_items
        if (this.editForm.checklist_items && this.editForm.checklist_items.length > 0) {
          input.checklist_items = this.editForm.checklist_items.map(item => ({
            id: item.id || null,
            title: item.title,
            is_completed: item.is_completed || false,
          }));
        } else {
          input.checklist_items = [];
        }

        // Remove null values
        Object.keys(input).forEach(key => {
          if (input[key] === null || input[key] === undefined) {
            delete input[key];
          }
        });

        await this.mutateTaskUpdate(input);
      } catch (error) {
        console.error('Error updating task:', error);
        if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update task. Please try again.' });
      }
    },
    addChecklistItem() {
      const title = (this.newChecklistItem || '').trim();
      if (!title) return;
      if (!this.editForm.checklist_items) {
        this.editForm.checklist_items = [];
      }
      this.editForm.checklist_items.push({ id: null, title, is_completed: false });
      this.newChecklistItem = '';
      this.updateTask();
    },
    removeChecklistItem(index) {
      this.editForm.checklist_items.splice(index, 1);
      this.updateTask();
    },
    toggleChecklistItem(index) {
      const item = this.editForm.checklist_items[index];
      if (item) {
        item.is_completed = !item.is_completed;
        this.updateTask();
      }
    },
    async addComment() {
      if (!this.newComment.trim()) return;

      this.commentLoading = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation AddTaskComment($input: CreateTaskCommentInput!) {
              addTaskComment(input: $input) {
                error
                error_code
                message
                data {
                  id
                  comment
                }
              }
            }
          `,
          variables: {
            input: {
              task_id: this.taskId,
              comment: this.newComment,
            },
          },
        });

        if (data.addTaskComment.error) {
          if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: data.addTaskComment.message });
        } else {
          this.newComment = '';
          this.$apollo.queries.task.refetch();
          if (this.hasTaskLogHistoryPermission && this.$apollo.queries.taskActivityLogs) {
            this.$apollo.queries.taskActivityLogs.refetch();
          }
        }
      } catch (error) {
        console.error('Error adding comment:', error);
        if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to add comment. Please try again.' });
      } finally {
        this.commentLoading = false;
      }
    },
    isCommentOwner(comment) {
      if (!comment || !comment.user || !comment.user.id || !this.currentUserId) return false;
      return parseInt(comment.user.id) === parseInt(this.currentUserId);
    },
    startEditComment(comment) {
      this.editingCommentId = comment.id;
      this.editingCommentText = comment.comment || '';
    },
    cancelEditComment() {
      this.editingCommentId = null;
      this.editingCommentText = '';
    },
    async saveEditComment() {
      if (!this.editingCommentId || !this.editingCommentText.trim()) return;

      this.commentLoading = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTaskComment($id: ID!, $comment: String!) {
              updateTaskComment(id: $id, comment: $comment) {
                error
                error_code
                message
                data { id comment }
              }
            }
          `,
          variables: {
            id: this.editingCommentId,
            comment: this.editingCommentText.trim(),
          },
        });

        if (data.updateTaskComment.error) {
          if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: data.updateTaskComment.message });
        } else {
          this.cancelEditComment();
          this.$apollo.queries.task.refetch();
          if (this.hasTaskLogHistoryPermission && this.$apollo.queries.taskActivityLogs) {
            this.$apollo.queries.taskActivityLogs.refetch();
          }
        }
      } catch (error) {
        console.error('Error updating comment:', error);
        if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update comment. Please try again.' });
      } finally {
        this.commentLoading = false;
      }
    },
    async confirmDeleteComment(comment) {
      const result = await this.$swal.fire({
        title: 'Delete comment?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTaskComment($id: ID!) {
              deleteTaskComment(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: comment.id,
          },
        });

        if (data.deleteTaskComment.error) {
          if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: data.deleteTaskComment.message });
        } else {
          this.$apollo.queries.task.refetch();
          if (this.hasTaskLogHistoryPermission && this.$apollo.queries.taskActivityLogs) {
            this.$apollo.queries.taskActivityLogs.refetch();
          }
          if (this.$swal) await this.$swal.fire({ icon: 'success', title: 'Deleted', text: 'Comment deleted successfully.', timer: 1500, showConfirmButton: false });
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
        if (this.$swal) await this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete comment. Please try again.' });
      }
    },
    async confirmDeleteTask() {
      if (!this.taskData || !this.taskData.id) return;

      const title = this.taskData.title || 'this task';
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete "${title}". This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTask($id: ID!) {
              deleteTask(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: this.taskId,
          },
        });

        const payload = data && data.deleteTask;
        if (payload && payload.error) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: payload.message || 'Failed to delete task.'
          });
          return;
        }

        // Show success toast
        this.showToast(payload && payload.message || 'Task deleted successfully');

        // Emit event to refresh board and close modal
        this.$emit('deleted');
        this.$emit('close');
      } catch (error) {
        console.error('Error deleting task:', error);
        await this.$swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Failed to delete task. Please try again.'
        });
      }
    },
    showToast(message = '') {
      const Toast = this.$swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', this.$swal.stopTimer);
          toast.addEventListener('mouseleave', this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: 'success',
        title: message
      });
    },
    async viewAttachment(attachment) {
      this.previewAttachment = attachment;
      this.previewLoading = true;
      this.previewError = null;
      this.previewUrl = null;
      this.previewHtml = null;
      this.useMammothFallback = false;
      this.showPreviewModal = true;

      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation DownloadTaskAttachment($id: ID!) {
              downloadTaskAttachment(id: $id) {
                error
                error_code
                message
                data {
                  download_url
                  attachment {
                    id
                    file_name
                    file_type
                  }
                }
              }
            }
          `,
          variables: {
            id: attachment.id
          }
        });

        if (!response.data.downloadTaskAttachment.error) {
          const downloadUrl = response.data.downloadTaskAttachment.data.download_url;

          // Handle DOCX and other Office files
          if (this.isDocxFile(attachment.file_type) || this.isOfficeDocument(attachment.file_type)) {
            // First priority: Try Microsoft Office Online viewer
            try {
              const encodedUrl = encodeURIComponent(downloadUrl);
              const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
              this.previewUrl = officeViewerUrl;
            } catch (officeError) {
              console.error('Error setting up Office Online viewer:', officeError);
              // Fall back to mammoth for DOCX files only
              if (this.isDocxFile(attachment.file_type)) {
                try {
                  const conversionResult = await this.convertDocxToHtml(attachment.id);
                  this.previewHtml = conversionResult.html;
                  this.useMammothFallback = true;
                  this.previewUrl = null;

                  if (conversionResult.messages && conversionResult.messages.length > 0) {
                    console.warn('DOCX conversion messages:', conversionResult.messages);
                  }
                } catch (conversionError) {
                  console.error('Error converting DOCX:', conversionError);
                  this.previewError = 'Failed to load document preview. Please download the file instead.';
                }
              } else {
                this.previewUrl = downloadUrl;
              }
            }
          } else {
            // For other file types, use the URL directly
            this.previewUrl = downloadUrl;
          }
        } else {
          this.previewError = response.data.downloadTaskAttachment.message;
        }
      } catch (error) {
        console.error('Error loading attachment for preview:', error);
        this.previewError = 'Failed to load the attachment for preview. Please try again.';
      } finally {
        this.previewLoading = false;
      }
    },
    async downloadAttachment(attachment) {
      try {
        await this.forceDownload(attachment.id, attachment.file_name);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Download Failed',
          text: 'Failed to download the attachment. Please try again.'
        });
      }
    },
    async forceDownload(attachmentId, filename) {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskAttachmentContent($id: ID!) {
              getTaskAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment {
                    id
                    file_name
                  }
                }
              }
            }
          `,
          variables: {
            id: attachmentId
          }
        });

        if (response.data.getTaskAttachmentContent.error) {
          throw new Error(response.data.getTaskAttachmentContent.message);
        }

        // Convert base64 content to blob
        const base64Content = response.data.getTaskAttachmentContent.data.content;
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob from binary data
        const blob = new Blob([bytes], { type: response.data.getTaskAttachmentContent.data.file_type });

        // Create object URL from blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up object URL
        window.URL.revokeObjectURL(blobUrl);

      } catch (error) {
        console.error('Error downloading file:', error);
        throw error;
      }
    },
    closePreviewModal() {
      this.showPreviewModal = false;
      this.previewAttachment = null;
      this.previewUrl = null;
      this.previewHtml = null;
      this.previewError = null;
      this.useMammothFallback = false;
    },
    async convertDocxToHtml(attachmentId) {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskAttachmentContent($id: ID!) {
              getTaskAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment {
                    id
                    file_name
                  }
                }
              }
            }
          `,
          variables: {
            id: attachmentId
          }
        });

        if (response.data.getTaskAttachmentContent.error) {
          throw new Error(response.data.getTaskAttachmentContent.message);
        }

        // Convert base64 content to ArrayBuffer
        const base64Content = response.data.getTaskAttachmentContent.data.content;
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const arrayBuffer = bytes.buffer;

        // Convert docx to HTML using mammoth
        const result = await mammoth.convertToHtml({
          arrayBuffer: arrayBuffer,
          styleMap: [
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "p[style-name='Title'] => h1.title:fresh",
            "p[style-name='Subtitle'] => h2.subtitle:fresh",
            "p[style-name='Default'] => p:fresh",
            "p[style-name='Normal (Web)'] => p:fresh",
            "r[style-name='Strong'] => strong",
            "r[style-name='Emphasis'] => em"
          ],
          includeEmbeddedStyleMap: true,
          includeDefaultStyleMap: true,
          preserveStyles: true,
        });

        return {
          html: result.value,
          messages: result.messages
        };
      } catch (error) {
        console.error('Error converting docx to HTML:', error);
        throw new Error('Failed to convert document to HTML: ' + error.message);
      }
    },
    isImageFile(fileType) {
      const imageTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'image/svg+xml',
        'image/tiff',
        'image/tif'
      ];
      return imageTypes.includes(fileType);
    },
    isPdfFile(fileType) {
      return fileType === 'application/pdf';
    },
    isTextFile(fileType) {
      const textTypes = [
        'text/plain',
        'text/html',
        'text/css',
        'text/javascript',
        'text/csv',
        'application/json',
        'application/xml',
        'text/xml'
      ];
      return textTypes.includes(fileType);
    },
    isOfficeDocument(fileType) {
      const officeTypes = [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ];
      return officeTypes.includes(fileType);
    },
    isDocxFile(fileType) {
      const docxTypes = [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      return docxTypes.includes(fileType);
    },
  },
};
</script>

<style lang="scss" scoped>
.max-h-90vh {
  max-height: 90vh;
}

.preview-container {
  height: 70vh;
  display: flex;
  flex-direction: column;
}

.preview-content {
  min-height: 200px;
}

.pdf-preview iframe,
.text-preview iframe,
.office-preview iframe {
  min-height: 70vh;
}

.image-preview img {
  max-height: 60vh;
  object-fit: contain;
}

.docx-wrapper {
  background: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin: 0 auto;
  max-width: 800px;
  min-height: 400px;
}

.docx-content {
  font-family: 'Times New Roman', Times, serif;
  line-height: 1.6;
  color: #000;
  font-size: 12pt;
  max-width: 100%;
}

.docx-content h1,
.docx-content h2,
.docx-content h3,
.docx-content h4,
.docx-content h5,
.docx-content h6 {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: bold;
  color: #000;
  text-align: center;
}

.docx-content h1.title {
  font-size: 16pt;
  font-weight: bold;
  text-align: center;
  margin: 1em 0;
}

.docx-content p {
  margin-bottom: 0.5em;
  text-align: center;
}

.docx-content strong {
  font-weight: bold;
}

.docx-content em {
  font-style: italic;
}

.docx-content ul,
.docx-content ol {
  margin-bottom: 1em;
  padding-left: 2em;
}

.docx-content table {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1em;
  border: 1px solid #000;
}

.docx-content table th,
.docx-content table td {
  border: 1px solid #000;
  padding: 8px;
  text-align: center;
  vertical-align: middle;
  background-color: #fff;
}

.docx-content table th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.docx-content img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

/* Progress bar animation - diagonal stripes + shimmer */
@keyframes task-progress-flow {
  0% {
    background-position: 0 0, -100% 0;
  }

  100% {
    background-position: 24px 0, 200% 0;
  }
}

.task-progress-bar-fill {
  background-image:
    repeating-linear-gradient(105deg,
      transparent 0,
      transparent 4px,
      rgba(255, 255, 255, 0.2) 4px,
      rgba(255, 255, 255, 0.2) 8px),
    linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 45%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0.35) 55%,
      transparent 100%);
  background-size: 24px 100%, 200% 100%;
  background-position: 0 0, -100% 0;
}

.task-progress-bar-fill--animated {
  animation: task-progress-flow 1.2s linear infinite;
}

.task-progress-bar-fill--static {
  background-image: none !important;
  background-size: unset !important;
  background-position: unset !important;
}

/* Time & Progress - prevent layout shift when values update */
.time-display-tabular {
  font-variant-numeric: tabular-nums;
}

/* Softer "Going on" indicator - opacity only, no layout shift */
@keyframes timer-dot-glow {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.6;
  }
}

.timer-dot {
  animation: timer-dot-glow 2s ease-in-out infinite;
}
</style>